import { StyleSheet } from 'aphrodite';
import { brandPrimary, charmGray, error, white } from '../../../../web/styles/colors';

export const styleSheet = StyleSheet.create({
	container: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		padding: '16px',
		backgroundColor: white,
		overflow: 'hidden',
	},
	header: {
		borderBottom: `1px solid ${charmGray}`,
		paddingBottom: '12px',
		marginBottom: '16px',
	},
	headerWithBack: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: '8px',
	},
	backButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		padding: 0,
		border: 'none',
		borderRadius: '6px',
		backgroundColor: 'transparent',
		color: brandPrimary,
		cursor: 'pointer',
		flexShrink: 0,
		':hover': {
			backgroundColor: 'rgba(0, 170, 232, 0.1)',
		},
	},
	title: {
		fontSize: '18px',
		fontWeight: 600,
		color: brandPrimary,
		margin: 0,
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
	},
	subtitle: {
		fontSize: '14px',
		color: charmGray,
		margin: '4px 0 0 0',
	},
	messagesContainer: {
		flex: 1,
		minHeight: 0,
		overflowY: 'auto',
		display: 'flex',
		flexDirection: 'column',
		gap: '12px',
	},
	// Assistant (AI Coach) message styles
	assistantMessage: {
		padding: '12px',
		borderRadius: '8px',
		backgroundColor: '#f5f5f5',
		border: '1px solid #e0e0e0',
	},
	assistantMessageLabel: {
		fontSize: '12px',
		fontWeight: 600,
		color: brandPrimary,
		textTransform: 'uppercase',
	},
	// User (Transcript Sent) message styles
	userMessage: {
		backgroundColor: '#f0f4f8',
		borderRadius: 8,
		padding: 12,
		marginBottom: 8,
		marginLeft: 24,
	},
	userMessageLabel: {
		fontSize: 11,
		fontWeight: 600,
		color: '#6b7280',
		marginBottom: 4,
		textTransform: 'uppercase' as const,
	},
	// System message styles (visual separator between call sessions)
	systemMessage: {
		padding: '8px 12px',
		textAlign: 'center' as const,
		borderTop: '1px solid #e0e0e0',
		borderBottom: '1px solid #e0e0e0',
		backgroundColor: '#fafafa',
	},
	systemMessageText: {
		fontSize: '12px',
		color: charmGray,
		fontStyle: 'italic',
	},
	// Legacy messageCard (keeping for historical messages view)
	messageCard: {
		padding: '12px',
		borderRadius: '8px',
		backgroundColor: '#f5f5f5',
		border: '1px solid #e0e0e0',
	},
	messageHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: '8px',
	},
	messageLabel: {
		fontSize: '12px',
		fontWeight: 600,
		color: brandPrimary,
		textTransform: 'uppercase',
	},
	messageTimestamp: {
		fontSize: '11px',
		color: charmGray,
	},
	messageContent: {
		fontSize: '14px',
		lineHeight: '1.5',
		color: '#333',
	},
	streamingIndicator: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: '4px',
		fontSize: '11px',
		color: brandPrimary,
		marginLeft: '8px',
	},
	emptyState: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		height: '100%',
		color: charmGray,
		textAlign: 'center',
		padding: '24px',
	},
	emptyStateIcon: {
		fontSize: '48px',
		marginBottom: '16px',
	},
	loadingContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: '8px',
		padding: '24px',
		color: charmGray,
	},
	errorMessage: {
		padding: '12px',
		borderRadius: '8px',
		backgroundColor: '#fff3f3',
		border: `1px solid ${error}`,
		color: error,
		fontSize: '14px',
	},
	// Live transcript area
	liveTranscriptArea: {
		padding: '12px',
		backgroundColor: '#fafafa',
		borderTop: `1px solid ${charmGray}`,
		borderBottom: `1px solid ${charmGray}`,
		maxHeight: '120px',
		overflowY: 'auto',
	},
	liveTranscriptHeader: {
		fontSize: '11px',
		fontWeight: 600,
		color: charmGray,
		marginBottom: '8px',
		textTransform: 'uppercase',
		letterSpacing: '0.5px',
	},
	utterance: {
		marginBottom: '6px',
		fontSize: '13px',
		lineHeight: '1.4',
	},
	speakerLabel: {
		fontWeight: 600,
		marginRight: '6px',
	},
	speakerLead: {
		color: '#2196F3',
	},
	speakerSalesRep: {
		color: '#4CAF50',
	},
	finalText: {
		color: '#333',
	},
	interimText: {
		color: charmGray,
		fontStyle: 'italic',
	},
	// Stable text - confirmed transcription
	stableText: {
		color: '#333',
	},
	// Speculative text - may change (visually distinct)
	speculativeText: {
		color: charmGray,
		fontStyle: 'italic',
		opacity: 0.7,
	},
	// Typing indicator for in-progress utterances
	typingIndicator: {
		display: 'inline-block',
		marginLeft: '4px',
		color: charmGray,
		fontSize: '12px',
		animation: 'pulse 1.5s infinite',
	},
	// Button container
	buttonContainer: {
		padding: '12px',
		borderTop: `1px solid ${charmGray}`,
		backgroundColor: white,
		marginTop: 'auto',
	},
	getSuggestionButton: {
		width: '100%',
		padding: '10px 16px',
		fontSize: '14px',
		fontWeight: 600,
		color: white,
		backgroundColor: brandPrimary,
		border: 'none',
		borderRadius: '6px',
		cursor: 'pointer',
		transition: 'background-color 0.2s',
		':hover': {
			backgroundColor: '#0095c8',
		},
		':disabled': {
			backgroundColor: charmGray,
			cursor: 'not-allowed',
		},
	},
});
